import { APPROVAL, convertNumber, useAccounts, useProject, useTaxCode, useToggle } from "utils"
import { BillServiceValues, detail_bill_service_values, DetailBillServiceValues } from "../types"
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect } from "react"
import { ActionButton, Button, Input, NumberInput, Select } from "components"
import { TbEye } from "react-icons/tb"
import { useProductUnit } from "pages/Inventori/Transaction/AddNewItem/components/FormSection/utils"

type TableListFormProps = {
  getFromState?: boolean
  disable?: boolean
  default_values?: DetailBillServiceValues
  onCancel?: () => void
  onCreate?: (value: DetailBillServiceValues) => void
  onUpdate?: (value: DetailBillServiceValues) => void
}

export const TableListForm = ({ getFromState, disable, default_values, onCancel, onCreate, onUpdate }: TableListFormProps) => {
  const dataAccount = useAccounts()
  const dataUnit = useProductUnit("name")
  const dataProject = useProject()
  const dataTaxCode = useTaxCode()

  const { isActive, toggle } = useToggle(getFromState ? Boolean(getFromState) : Boolean(default_values))

  const { watch } = useFormContext<BillServiceValues>()
  const isView = watch("approval_status") === APPROVAL.APPROVED || watch("approval_status") === APPROVAL.REJECTED

  const methods = useForm<DetailBillServiceValues>({
    defaultValues: default_values! ?? {
      product_id: "",
      description: "",
      quantity_order: 0,
      quantity_unit: 0,
      price_bill: 0,
      discount: 0,
      total: 0,
      project_id: "",
      tax_id: ""
    },
    resolver: yupResolver(detail_bill_service_values)
  })
  const onButtonClickHandler = (value: DetailBillServiceValues) => {
    if (onCreate) {
      methods.reset()
      toggle()

      onCreate(value)
    }
  }
  const onButtonUpdateHandler = (value: DetailBillServiceValues) => {
    if (onUpdate) onUpdate(value)
  }

  const quantity_order = methods.watch("quantity_order")
  const price_bill = methods.watch("price_bill")
  const discount = methods.watch("discount")
  const total_ill = methods.watch("total")
  const product_id = methods.watch("product_id")

  const getAccountName = dataAccount?.data?.find(acc => acc?.value === product_id)?.label
  const matchAccount = getAccountName?.match(/^([\d-]+)\s+(.+)$/)
  const accountName = matchAccount?.[2]

  useEffect(() => {
    const total = (quantity_order ?? 0) * (price_bill ?? 0) * (1 - (discount ?? 0) / 100)
    methods.setValue("total", total)

    // eslint-disable-next-line
  }, [quantity_order, price_bill, discount])

  if (isActive) {
    return (
      <FormProvider {...methods}>
        <tr className="no-padding-body">
          <td />
          <td className="min-w-[200px]">
            <Controller
              name="product_id"
              control={methods.control}
              render={({ field, fieldState }) => (
                <Select
                  placeholder="Select account"
                  error={fieldState.error?.message}
                  isLoading={dataAccount.isLoading}
                  options={dataAccount?.data}
                  ref={field.ref}
                  value={dataAccount?.data?.find(item => item.value === field.value) ?? null}
                  onChange={res => field.onChange(res?.value)}
                />
              )}
            />
          </td>
          <td>{accountName ?? "-"}</td> 
          <td className="min-w-[200px] !px-3">
            <Controller
              control={methods.control}
              name="description"
              render={({ field, fieldState }) => (
                <Input
                  error={fieldState.error?.message}
                  ref={field.ref}
                  value={field.value}
                  onChange={(e) => {
                    const val = e.target.value
                    field.onChange(val)
                
                    if (val === "") {
                      methods.setError("description", {
                        type: "manual",
                        message: "Description is required"
                      })
                    } else {
                      methods.clearErrors("description")
                    }
                  }}
                />
              )}
            />
          </td>
          <Controller
            name="quantity_order"
            control={methods.control}
            render={({ field, fieldState }) => (
              <td className="min-w-[200px]">
                <NumberInput
                  error={fieldState.error?.message}
                  ref={field.ref}
                  value={field.value}
                  onValueChange={e => field.onChange(e.floatValue)}
                />
              </td>
            )}
          />
          <td className="min-w-[200px]">
            <Controller
              name="mr_unit_id"
              control={methods.control}
              render={({ field, fieldState }) => (
                <Select
                  placeholder="Select unit"
                  error={fieldState.error?.message}
                  isLoading={dataUnit?.isLoadingUnit}
                  options={dataUnit?.dataUnit}
                  ref={field.ref}
                  value={dataUnit?.dataUnit?.find(item => item?.value?.toString() === field?.value?.toString()) ?? null}
                  onChange={res => field.onChange(res?.value)}
                />
              )}
            />
          </td>
          <td className="min-w-[200px]">
            <Controller
              name="price_bill"
              control={methods.control}
              render={({ field, fieldState }) => (
                <NumberInput
                  textRight
                  ref={field.ref}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value.floatValue)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </td>
          <td className="min-w-[200px]">
            <Controller
              name="discount"
              control={methods.control}
              render={({ field, fieldState }) => (
                <NumberInput
                  textRight
                  suffix="%"
                  ref={field.ref}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value.floatValue)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </td>
          <td className="min-w-[200px]">
            <Controller
              name="total"
              control={methods.control}
              render={({ field }) => <div className="px-3 text-right">{convertNumber(field?.value!).intoCurrency}</div>}
            />
          </td>
          <td className="min-w-[200px]">
            <Controller
              control={methods.control}
              name="project_id"
              render={({ field, fieldState }) => (
                <Select
                  placeholder="Select Job"
                  error={fieldState.error?.message}
                  isLoading={dataProject.isReloading}
                  options={dataProject?.data}
                  ref={field.ref}
                  value={dataProject?.data?.find(item => item.value === field.value) ?? null}
                  onChange={item => field.onChange(item?.value)}
                />
              )}
            />
          </td>
          <td className="min-w-[200px]">
            <Controller
              control={methods.control}
              name="tax_id"
              render={({ field, fieldState }) => (
                <Select
                  placeholder="Select Tax"
                  error={fieldState.error?.message}
                  isLoading={dataTaxCode.isReloading}
                  options={dataTaxCode?.data}
                  ref={field.ref}
                  value={dataTaxCode?.data?.find(item => item.value === field.value) ?? null}
                  onChange={item => {
                    const match = item?.label.match(/([\d.]+)%/);
                    const taxPercent = match ? parseFloat(match[1]) : 0;
                    const taxAmount = ((total_ill ?? 0) * (taxPercent ?? 0)) / 100;

                    methods.setValue("total_tax_paid", taxAmount)
                    methods.setValue("tax_rate", String(taxPercent))

                    field.onChange(item?.value)
                  }}
                />
              )}
            />
          </td>
          <td className="text-center">
            {default_values ? (
              <section className="flex justify-center">
                <ActionButton.Check
                  type="button"
                  onClick={methods.handleSubmit(onButtonUpdateHandler)}
                />
                <ActionButton.Close type="button" onClick={onCancel} />
              </section>
            ) : (
              <ActionButton.Add
                type="button"
                onClick={methods.handleSubmit(onButtonClickHandler)}
              />
            )}
          </td>
        </tr>
      </FormProvider>
    )
  }

  return (
    <tr className="no-padding-body">
      <td className="text-center">
        {!isView && !disable && (
          <Button color="transparent" onClick={toggle}>
            <TbEye className="size-5" />
          </Button>
        )}
      </td>

      <td colSpan={12} />
    </tr>
  )
}